/* WebNest — shared NS* form/modal helpers (ported from the redesign prototype).
   These wrap the design-system CSS classes/vars already defined in index.css so
   ported screens render 1:1 with the prototype. */
import React, { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { I, Icon } from './ns-icons'

/* ---------------- modal shell ---------------- */
export function NSModal({ icon, tone, title, subtitle, onClose, width = 620, foot, children }) {
  return (
    <div className="drawer-backdrop" style={{ justifyContent: 'center', alignItems: 'center', animation: 'fadeIn 0.16s ease' }} onClick={onClose}>
      <div className="card" style={{ width, maxWidth: '94vw', maxHeight: '92vh', display: 'flex', flexDirection: 'column', boxShadow: 'var(--shadow-lg)', animation: 'slideDown 0.22s cubic-bezier(0.22,1,0.36,1)' }} onClick={(e) => e.stopPropagation()}>
        <div style={{ padding: '18px 22px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
          {icon && <span className={`icon-tile ${tone || ''}`} style={{ width: 36, height: 36, flexShrink: 0 }}><Icon name={icon} /></span>}
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2 style={{ fontSize: 17, fontWeight: 600, letterSpacing: '-0.01em' }}>{title}</h2>
            {subtitle && <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 2 }}>{subtitle}</div>}
          </div>
          <button className="site-action" onClick={onClose}><I.x /></button>
        </div>
        <div style={{ padding: 22, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 18 }}>
          {children}
        </div>
        {foot && (
          <div style={{ padding: '14px 22px', borderTop: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
            {foot}
          </div>
        )}
      </div>
    </div>
  )
}

/* small reusable chip-toggle for multi-select access lists */
export function NSChip({ label, on, onClick, icon }) {
  return (
    <button type="button" onClick={onClick} style={{
      display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 11px', borderRadius: 999,
      fontSize: 12, fontWeight: 500, cursor: 'pointer', transition: 'all 0.13s ease',
      border: `1px solid ${on ? 'var(--border-accent)' : 'var(--border)'}`,
      background: on ? 'var(--accent-soft)' : 'var(--surface)',
      color: on ? 'var(--accent-3)' : 'var(--text-soft)',
    }}>
      {on ? <I.check style={{ fontSize: 13 }} /> : icon ? <Icon name={icon} style={{ fontSize: 13 }} /> : null}
      {label}
    </button>
  )
}

/* ---------------- form fields ---------------- */
export function NSToggle({ on, onChange }) {
  const [state, setState] = useState(!!on)
  const v = onChange ? on : state
  const flip = (e) => { e.stopPropagation(); onChange ? onChange(!on) : setState(!state) }
  return (
    <button type="button" onClick={flip} style={{
      width: 34, height: 19, borderRadius: 999, flexShrink: 0,
      background: v ? 'var(--accent)' : 'var(--surface-2)',
      position: 'relative', transition: 'background 0.15s ease',
      border: '1px solid var(--border)',
    }}>
      <span style={{
        position: 'absolute', top: 1, left: v ? 16 : 1,
        width: 15, height: 15, borderRadius: '50%', background: 'white',
        transition: 'left 0.15s ease', boxShadow: '0 1px 2px rgba(0,0,0,0.3)',
      }} />
    </button>
  )
}

export function NSField({ label, hint, children, full }) {
  return (
    <div style={{ gridColumn: full ? '1 / -1' : 'auto', display: 'flex', flexDirection: 'column', gap: 7, minWidth: 0 }}>
      <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-soft)' }}>
        {label}{hint && <span style={{ color: 'var(--text-faint)', fontWeight: 400 }}> · {hint}</span>}
      </label>
      {children}
    </div>
  )
}

export function NSPrefix({ prefix, value, defaultValue, placeholder, onChange }) {
  return (
    <div style={{ display: 'flex' }}>
      <span style={{ padding: '9px 12px', background: 'var(--surface)', border: '1px solid var(--border)', borderRight: 0, borderRadius: '10px 0 0 10px', color: 'var(--text-muted)', fontSize: 13, fontFamily: 'var(--font-mono)', whiteSpace: 'nowrap' }}>{prefix}</span>
      <input className="input" value={value} defaultValue={defaultValue} placeholder={placeholder} onChange={onChange} style={{ borderRadius: '0 10px 10px 0' }} />
    </div>
  )
}

// Custom dropdown that replaces the native <select>. Native option menus render
// with OS styling (e.g. white-on-white on Windows/Chrome in dark mode); this
// uses the same themed card/menu as the rest of the app and stays consistent.
// onChange is called with an event-shaped arg ({ target: { value } }) so existing
// `onChange={e => set(e.target.value)}` call sites keep working unchanged.
export function NSSelect({ value, defaultValue, options, onChange, placeholder = 'Select…', disabled }) {
  const opts = (options || []).map((o) => (typeof o === 'object' ? o : { value: o, label: o }))
  const [internal, setInternal] = useState(defaultValue ?? '')
  const current = value !== undefined ? value : internal
  const selected = opts.find((o) => String(o.value) === String(current))

  const [open, setOpen] = useState(false)
  const [pos, setPos] = useState(null) // null until measured, so we never flash at 0,0
  const anchorRef = useRef(null)
  const menuRef = useRef(null)

  const close = useCallback(() => setOpen(false), [])

  // Anchor the menu under (or above, when there's no room) the trigger, clamped
  // to the viewport — same approach as the app's other portal menus.
  const place = useCallback(() => {
    const a = anchorRef.current
    if (!a) return
    const r = a.getBoundingClientRect()
    const menuH = menuRef.current?.offsetHeight || 0
    const menuW = Math.max(menuRef.current?.offsetWidth || 0, r.width)
    const gap = 6
    let top = r.bottom + gap
    if (menuH && top + menuH > window.innerHeight - 8) {
      const above = r.top - gap - menuH
      top = above >= 8 ? above : Math.max(8, window.innerHeight - menuH - 8)
    }
    let left = Math.max(8, Math.min(r.left, window.innerWidth - menuW - 8))
    setPos({ top, left, width: r.width })
  }, [])

  useLayoutEffect(() => {
    if (!open) return
    place()
    const onMove = () => place()
    window.addEventListener('scroll', onMove, true)
    window.addEventListener('resize', onMove)
    return () => {
      window.removeEventListener('scroll', onMove, true)
      window.removeEventListener('resize', onMove)
    }
  }, [open, place])

  useEffect(() => {
    if (!open) return
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false) }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open])

  const pick = (v) => {
    if (value === undefined) setInternal(v)
    onChange?.({ target: { value: v } })
    setOpen(false)
  }

  return (
    <div ref={anchorRef} style={{ position: 'relative', width: '100%' }}>
      <button
        type="button"
        className="input"
        disabled={disabled}
        onClick={() => { if (disabled) return; if (!open) setPos(null); setOpen((o) => !o) }}
        style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'left', cursor: disabled ? 'not-allowed' : 'pointer' }}
      >
        <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: selected ? 'var(--text)' : 'var(--text-faint)' }}>
          {selected ? selected.label : placeholder}
        </span>
        <I.chevronDown style={{ fontSize: 14, color: 'var(--text-faint)', flexShrink: 0 }} />
      </button>
      {open && createPortal(
        <>
          <div onClick={close} style={{ position: 'fixed', inset: 0, zIndex: 4000 }} />
          <div
            ref={menuRef}
            className="card"
            style={{ position: 'fixed', top: pos?.top ?? 0, left: pos?.left ?? 0, minWidth: pos?.width ?? 160, zIndex: 4001, padding: 6, maxHeight: 280, overflowY: 'auto', boxShadow: '0 10px 30px rgba(0,0,0,0.22)', visibility: pos ? 'visible' : 'hidden' }}
          >
            {opts.length === 0 && (
              <div style={{ padding: '8px 10px', fontSize: 12.5, color: 'var(--text-faint)' }}>No options</div>
            )}
            {opts.map((o) => {
              const active = String(o.value) === String(current)
              return (
                <button
                  key={o.value}
                  type="button"
                  onClick={() => pick(o.value)}
                  style={{ display: 'flex', width: '100%', alignItems: 'center', gap: 9, padding: '8px 10px', borderRadius: 8, fontSize: 12.5, textAlign: 'left', background: active ? 'var(--surface-2)' : 'transparent', color: 'var(--text-soft)' }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--surface-2)' }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = active ? 'var(--surface-2)' : 'transparent' }}
                >
                  <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{o.label}</span>
                  {active && <I.check style={{ color: 'var(--accent)', fontSize: 13, flexShrink: 0 }} />}
                </button>
              )
            })}
          </div>
        </>,
        document.body
      )}
    </div>
  )
}

export function NSSegment({ options, value, onChange }) {
  return (
    <div className="segmented" style={{ width: '100%' }}>
      {options.map((o) => (
        <button type="button" key={o} className={value === o ? 'on' : ''} style={{ flex: 1, justifyContent: 'center', padding: '8px 9px' }} onClick={() => onChange(o)}>{o}</button>
      ))}
    </div>
  )
}

export function NSDrop({ icon = 'upload', title, hint, height = 130, accent, onClick, onFiles }) {
  const inputRef = React.useRef(null)
  const handleClick = () => { if (onFiles) inputRef.current?.click(); if (onClick) onClick() }
  return (
    <div onClick={handleClick} onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); if (onFiles) onFiles(Array.from(e.dataTransfer.files)) }} style={{
      height, borderRadius: 12, border: '1.5px dashed var(--border-strong)',
      background: accent ? 'var(--accent-soft)' : 'var(--surface)',
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      gap: 9, textAlign: 'center', padding: 16, cursor: 'pointer', transition: 'all 0.15s ease',
    }}>
      {onFiles && <input ref={inputRef} type="file" multiple style={{ display: 'none' }} onChange={(e) => { onFiles(Array.from(e.target.files)); e.target.value = '' }} />}
      <span className="icon-tile" style={{ width: 38, height: 38, fontSize: 17 }}><Icon name={icon} /></span>
      <div style={{ fontSize: 12.5, color: 'var(--text-soft)', fontWeight: 500 }}>{title}</div>
      {hint && <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{hint}</div>}
    </div>
  )
}
